// A list of usefull functions to handle color transformation, gradients, etc.
import { getContrastRatio } from '@mui/material'

const buildLinearGradient = ({ start, end }, angle = '180deg') => `linear-gradient(${angle}, ${start}, ${end})`

const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : hex
}

// Accessibility-safe brand colour resolver. Starting from `background`, it walks
// darker down the brand (primary) shade ramp until `background` clears the target
// contrast `ratio` against `foreground`, so foreground text/icons stay legible on
// any tenant brand colour. Shared by the Button variants
// (action-buttons/action-button/style.js) and the human-made badge
// (human-made-banner/colors.js).
// Note: the walk is anchored to palette.primary.shade, so a `background` that is
// not on that ramp restarts from its lightest entry.
// Returns `{ main: foreground, contrast: <accessible background> }`.
// Ramps hang off palette entries as `shade` maps ordered light -> dark. The brand
// ramp is tried first, then any other one (grey, publicTenantSecondary, ...), so a
// colour is darkened along its own scale rather than swapped for a brand tint.
const findShadeRamp = (palette, colour) => {
  const ramps = [palette?.primary?.shade, ...Object.values(palette ?? {}).map(entry => entry?.shade)]

  for (const ramp of ramps) {
    if (!ramp || typeof ramp !== 'object') continue
    const values = Object.values(ramp)
    const index = values.indexOf(colour)
    if (index !== -1) return { values, index }
  }

  return null
}

const getSufficientContrast = (palette, foreground, background, ratio = 4.5) => {
  const ramp = findShadeRamp(palette, background)
  // `background` sits on no ramp we can walk (a one-off hex, say) — fall back to
  // the brand ramp from its lightest entry so callers still get a readable pair.
  const values = ramp?.values ?? Object.values(palette.primary.shade)
  // Track the position rather than looking the current value up again each pass:
  // adjacent shades can round to the same colour (lighten/darken collapse the ends
  // of near-white and near-black brands) and indexOf would keep returning the first
  // of that run, leaving the cursor stuck and the loop spinning.
  let index = ramp?.index ?? -1

  let contrast = background
  while (getContrastRatio(foreground, contrast) < ratio) {
    index += 1
    // Exhausted the ramp without reaching the ratio — fall back to the darkest
    // shade rather than returning undefined so callers always get a usable pair.
    if (index >= values.length) break
    contrast = values[index]
  }

  return { main: foreground, contrast }
}

export {
  buildLinearGradient,
  hexToRgb,
  getSufficientContrast
}
